/**
 * Locale-aware date formatting. Browsers ship no Mongolian calendar data (`'mn'` resolves to
 * en-US), so the mn path is composed here; every other language goes to Intl exactly as before.
 */

type Opts = Intl.DateTimeFormatOptions

const MN_WEEKDAY_LONG = ['Ням', 'Даваа', 'Мягмар', 'Лхагва', 'Пүрэв', 'Баасан', 'Бямба']
const MN_WEEKDAY_SHORT = ['Ня', 'Да', 'Мя', 'Лх', 'Пү', 'Ба', 'Бя']
const EN_WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function isMongolian(language: string | undefined): boolean {
  return (language ?? '').toLowerCase().split('-')[0] === 'mn'
}

interface Parts {
  year: number
  month: number
  day: number
  weekday: number
  hour: number
  minute: number
  second: number
}

function partsOf(value: Date, timeZone: string | undefined): Parts {
  const raw = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(value)
  const get = (type: Intl.DateTimeFormatPartTypes) => raw.find((p) => p.type === type)?.value ?? ''
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    weekday: EN_WEEKDAY.indexOf(get('weekday')),
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
    second: Number(get('second')),
  }
}

interface Fields {
  weekday?: 'long' | 'short'
  year?: boolean
  month?: 'word' | 'number'
  day?: boolean
  time?: 'minutes' | 'seconds'
}

function fieldsOf(options: Opts, defaults: 'date' | 'datetime'): Fields {
  if (options.dateStyle || options.timeStyle) {
    const f: Fields = {}
    if (options.dateStyle) {
      f.year = true
      f.day = true
      f.month = options.dateStyle === 'short' ? 'number' : 'word'
      if (options.dateStyle === 'full') f.weekday = 'long'
    }
    if (options.timeStyle) f.time = options.timeStyle === 'short' ? 'minutes' : 'seconds'
    return f
  }
  const f: Fields = {}
  if (options.weekday) f.weekday = options.weekday === 'long' ? 'long' : 'short'
  if (options.year) f.year = true
  if (options.month) f.month = options.month === 'long' || options.month === 'short' || options.month === 'narrow' ? 'word' : 'number'
  if (options.day) f.day = true
  if (options.hour || options.minute) f.time = options.second ? 'seconds' : 'minutes'
  const any = f.weekday || f.year || f.month || f.day || f.time
  if (!any) {
    f.year = true
    f.month = 'number'
    f.day = true
    if (defaults === 'datetime') f.time = 'seconds'
  }
  return f
}

const pad = (n: number) => String(n).padStart(2, '0')

function mnDatePart(p: Parts, f: Fields): string {
  if (f.month === 'number') {
    return [f.year ? String(p.year) : null, pad(p.month), f.day ? pad(p.day) : null]
      .filter((s): s is string => s !== null)
      .join('.')
  }
  if (f.month === 'word') {
    const month = `${p.month}-р сар`
    const year = f.year ? `${p.year} оны ` : ''
    return f.day ? `${year}${month}ын ${p.day}` : `${year}${month}`
  }
  if (f.year && f.day) return `${p.year}.${pad(p.month)}.${pad(p.day)}`
  if (f.year) return String(p.year)
  if (f.day) return String(p.day)
  return ''
}

export function formatMongolian(value: Date, options: Opts = {}, defaults: 'date' | 'datetime' = 'date'): string {
  if (Number.isNaN(value.getTime())) return 'Invalid Date'
  const p = partsOf(value, options.timeZone)
  const f = fieldsOf(options, defaults)
  const date = mnDatePart(p, f)
  const weekday = f.weekday ? (f.weekday === 'long' ? MN_WEEKDAY_LONG : MN_WEEKDAY_SHORT)[p.weekday] ?? '' : ''
  const head = [weekday, date].filter(Boolean).join(', ')
  const time = f.time
    ? `${pad(p.hour)}:${pad(p.minute)}${f.time === 'seconds' ? `:${pad(p.second)}` : ''}`
    : ''
  return [head, time].filter(Boolean).join(' ')
}

/** Drop-in for `value.toLocaleDateString(language, options)`. */
export function formatDate(value: Date, language: string, options?: Opts): string {
  return isMongolian(language) ? formatMongolian(value, options, 'date') : value.toLocaleDateString(language, options)
}

/** Drop-in for `value.toLocaleString(language, options)`. */
export function formatDateTime(value: Date, language: string, options?: Opts): string {
  return isMongolian(language) ? formatMongolian(value, options, 'datetime') : value.toLocaleString(language, options)
}

/** Drop-in for `new Intl.DateTimeFormat(language, options)` where only `.format` is used. */
export function dateFormatter(language: string, options?: Opts): { format: (value: Date) => string } {
  if (isMongolian(language)) return { format: (value) => formatMongolian(value, options, 'date') }
  const fmt = new Intl.DateTimeFormat(language, options)
  return { format: (value) => fmt.format(value) }
}
